(function () {
  'use strict';

  const CONFIG = window.CustomCartLimit || { max: 10, message: 'Limit reached.' };
  const MAX = CONFIG.max;
  const MSG = CONFIG.message.replace('{max}', MAX);

  // ---------- Toast / notification ----------
  // Kalles/Horizon dispatches its own toast events; we try those first,
  // then fall back to a self-contained DOM toast so we never depend on
  // a specific component existing.
  function notify(text) {
    // Try Horizon-style event first
    document.dispatchEvent(new CustomEvent('cart:error', { detail: { message: text } }));

    // Fallback toast
    let el = document.getElementById('custom-cart-limit-toast');
    if (!el) {
      el = document.createElement('div');
      el.id = 'custom-cart-limit-toast';
      el.setAttribute('role', 'status');
      el.setAttribute('aria-live', 'polite');
      el.style.cssText = [
        'position:fixed', 'bottom:24px', 'left:50%',
        'transform:translateX(-50%)', 'background:#111', 'color:#fff',
        'padding:12px 18px', 'border-radius:8px', 'font:14px/1.4 system-ui',
        'box-shadow:0 6px 24px rgba(0,0,0,.18)', 'z-index:99999',
        'opacity:0', 'transition:opacity .2s', 'pointer-events:none',
        'max-width:90vw', 'text-align:center'
      ].join(';');
      document.body.appendChild(el);
    }
    el.textContent = text;
    requestAnimationFrame(() => (el.style.opacity = '1'));
    clearTimeout(el._t);
    el._t = setTimeout(() => (el.style.opacity = '0'), 3000);
  }

  // ---------- Cart state helper ----------
  async function getCart() {
    try {
      const r = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      return r.ok ? r.json() : { items: [] };
    } catch (_) {
      return { items: [] };
    }
  }

  function qtyInCart(cart, variantId) {
    const id = String(variantId);
    const item = (cart.items || []).find(i => String(i.variant_id) === id);
    return item ? item.quantity : 0;
  }

  // ---------- Parse various payload shapes ----------
  // Shopify AJAX accepts JSON, FormData, and URL-encoded bodies.
  async function parseBody(init) {
    if (!init || !init.body) return null;
    const body = init.body;

    if (typeof body === 'string') {
      // JSON?
      try { return JSON.parse(body); } catch (_) {}
      // URL-encoded
      const params = new URLSearchParams(body);
      const obj = {};
      params.forEach((v, k) => (obj[k] = v));
      return obj;
    }
    if (body instanceof FormData) {
      const obj = {};
      body.forEach((v, k) => (obj[k] = v));
      return obj;
    }
    if (body instanceof URLSearchParams) {
      const obj = {};
      body.forEach((v, k) => (obj[k] = v));
      return obj;
    }
    return null;
  }

  // ---------- Validators ----------
  async function validateAdd(payload) {
    if (!payload) return true;
    const cart = await getCart();

    // /cart/add.js supports either a single item or { items: [...] }
    const items = payload.items
      ? payload.items
      : [{ id: payload.id, quantity: payload.quantity }];

    for (const it of items) {
      const variantId = it.id;
      const adding = parseInt(it.quantity, 10) || 1;
      const existing = qtyInCart(cart, variantId);
      if (existing + adding > MAX) {
        notify(MSG);
        return false;
      }
    }
    return true;
  }

  async function validateChange(payload) {
    if (!payload) return true;
    // /cart/change.js accepts { id|line, quantity }
    const requested = parseInt(payload.quantity, 10);
    if (Number.isFinite(requested) && requested > MAX) {
      notify(MSG);
      return false;
    }
    return true;
  }

  async function validateUpdate(payload) {
    if (!payload || !payload.updates) return true;
    const updates = payload.updates;
    const values = Array.isArray(updates) ? updates : Object.values(updates);
    for (const v of values) {
      const n = parseInt(v, 10);
      if (Number.isFinite(n) && n > MAX) {
        notify(MSG);
        return false;
      }
    }
    return true;
  }

  // ---------- Fetch interception ----------
  const origFetch = window.fetch;
  window.fetch = async function (resource, init) {
    const url = typeof resource === 'string' ? resource : (resource && resource.url) || '';

    if (/\/cart\/add(\.js)?(\?|$)/.test(url)) {
      const ok = await validateAdd(await parseBody(init));
      if (!ok) {
        return new Response(
          JSON.stringify({ status: 422, message: MSG, description: MSG }),
          { status: 422, headers: { 'Content-Type': 'application/json' } }
        );
      }
    } else if (/\/cart\/change(\.js)?(\?|$)/.test(url)) {
      const ok = await validateChange(await parseBody(init));
      if (!ok) {
        const cart = await getCart();
        return new Response(JSON.stringify(cart), {
          status: 422,
          headers: { 'Content-Type': 'application/json' }
        });
      }
    } else if (/\/cart\/update(\.js)?(\?|$)/.test(url)) {
      const ok = await validateUpdate(await parseBody(init));
      if (!ok) {
        const cart = await getCart();
        return new Response(JSON.stringify(cart), {
          status: 422,
          headers: { 'Content-Type': 'application/json' }
        });
      }
    }

    return origFetch.apply(this, arguments);
  };

  // ---------- XHR interception (for any legacy/app code) ----------
  const OrigXHR = window.XMLHttpRequest;
  function PatchedXHR() {
    const xhr = new OrigXHR();
    const origOpen = xhr.open;
    const origSend = xhr.send;
    let _url = '';

    xhr.open = function (method, url) {
      _url = url || '';
      return origOpen.apply(xhr, arguments);
    };

    xhr.send = async function (body) {
      const isCartCall = /\/cart\/(add|change|update)(\.js)?(\?|$)/.test(_url);
      if (isCartCall) {
        const payload = await parseBody({ body });
        let ok = true;
        if (/\/cart\/add/.test(_url)) ok = await validateAdd(payload);
        else if (/\/cart\/change/.test(_url)) ok = await validateChange(payload);
        else if (/\/cart\/update/.test(_url)) ok = await validateUpdate(payload);
        if (!ok) {
          // Simulate a 422 response without hitting the network
          Object.defineProperty(xhr, 'readyState', { value: 4, configurable: true });
          Object.defineProperty(xhr, 'status', { value: 422, configurable: true });
          Object.defineProperty(xhr, 'responseText', {
            value: JSON.stringify({ message: MSG, description: MSG }),
            configurable: true
          });
          xhr.dispatchEvent(new Event('readystatechange'));
          xhr.dispatchEvent(new Event('load'));
          xhr.dispatchEvent(new Event('loadend'));
          return;
        }
      }
      return origSend.apply(xhr, arguments);
    };

    return xhr;
  }
  PatchedXHR.prototype = OrigXHR.prototype;
  window.XMLHttpRequest = PatchedXHR;

  // ---------- Input guard + max attribute injection ----------
  function clampInput(input) {
    if (!input || input.type !== 'number') return;
    if (!input.hasAttribute('data-custom-max-applied')) {
      input.setAttribute('max', MAX);
      input.setAttribute('data-custom-max-applied', '1');
    }
    const v = parseInt(input.value, 10);
    if (Number.isFinite(v) && v > MAX) {
      input.value = MAX;
      notify(MSG);
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  function isQuantityInput(el) {
    if (!el || el.tagName !== 'INPUT') return false;
    const name = (el.name || '').toLowerCase();
    return (
      name === 'quantity' ||
      name.startsWith('updates[') ||
      el.matches('[data-quantity-input], quantity-input input, .quantity__input')
    );
  }

  document.addEventListener('input', e => {
    if (isQuantityInput(e.target)) clampInput(e.target);
  }, true);

  document.addEventListener('change', e => {
    if (isQuantityInput(e.target)) clampInput(e.target);
  }, true);

  // Apply max attr to existing + future inputs
  function scan(root) {
    (root || document).querySelectorAll('input[type="number"]').forEach(el => {
      if (isQuantityInput(el)) clampInput(el);
    });
  }
  scan(document);
  new MutationObserver(muts => muts.forEach(m => m.addedNodes.forEach(n => {
    if (n.nodeType === 1) scan(n);
  }))).observe(document.documentElement, { childList: true, subtree: true });
})();